import React, { useState, useEffect } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { useFavorites } from '@/hooks/useFavorites';
import { useTranslation } from '@/hooks/useTranslation';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { ArrowLeft, Save, MapPin, Calendar, Heart, Mic, Route, Globe, Star, Users, Settings, ChevronRight } from 'lucide-react';
import { Podcast } from '@/types/Podcast';

interface CityTour {
  id: string;
  title: string;
  city: string;
  country: string;
  style: string;
  duration: string;
  created_at: string;
  description?: string;
  is_public?: boolean;
  latitude?: number;
  longitude?: number;
  museum_count?: number;
  creator_user_id: string;
  updated_at?: string;
}

const Profile = () => {
  const { user, profile, loading: authLoading } = useAuth();
  const { favorites } = useFavorites();
  const { language, toggleLanguage } = useTranslation();
  const { toast } = useToast();
  const navigate = useNavigate();
  
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [myPodcasts, setMyPodcasts] = useState<Podcast[]>([]);
  const [favoritePodcasts, setFavoritePodcasts] = useState<Podcast[]>([]);
  const [myTours, setMyTours] = useState<CityTour[]>([]);
  const [selectedLanguage, setSelectedLanguage] = useState<'FR' | 'EN'>(language);
  const [betaTester, setBetaTester] = useState(false);
  const [showBetaDialog, setShowBetaDialog] = useState(false);

  // Fetch user's created podcasts
  const fetchMyPodcasts = async () => {
    if (!user) return;
    
    try {
      const { data, error } = await supabase
        .from('podcasts')
        .select('*')
        .eq('user_id', user.id)
        .order('Name');
      
      if (error) throw error;
      setMyPodcasts(data || []);
    } catch (error) {
      console.error('Error fetching user podcasts:', error);
    }
  };

  // Fetch user's created tours
  const fetchMyTours = async () => {
    if (!user) return;
    
    try {
      const { data, error } = await supabase
        .from('city_tours')
        .select('*')
        .eq('creator_user_id', user.id)
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      
      // Map database columns to CityTour interface
      const mappedTours = (data || []).map(tour => ({
        id: tour.id,
        title: tour.description || `${tour['Nom ville']} Tour`,
        city: tour['Nom ville'],
        country: tour.country,
        style: tour.style,
        duration: tour.duration,
        created_at: tour.created_at,
        description: tour.description,
        is_public: tour.is_public,
        latitude: tour.latitude,
        longitude: tour.longitude,
        museum_count: tour.museum_count,
        creator_user_id: tour.creator_user_id,
        updated_at: tour.updated_at
      }));
      
      setMyTours(mappedTours);
    } catch (error) {
      console.error('Error fetching user tours:', error);
    }
  };

  // Fetch favorite podcasts details
  const fetchFavoritePodcasts = async () => {
    if (favorites.length === 0) {
      setFavoritePodcasts([]);
      return;
    }
    
    try {
      const { data, error } = await supabase
        .from('podcasts')
        .select('*')
        .in('Name', favorites)
        .order('Name');
      
      if (error) throw error;
      setFavoritePodcasts(data || []);
    } catch (error) {
      console.error('Error fetching favorite podcasts:', error);
    }
  };

  // Update form when profile or user data is available
  useEffect(() => {
    if (profile) {
      setFullName(profile.full_name || '');
      setEmail(profile.email || user?.email || '');
      setBetaTester(profile.beta_tester || false);
    } else if (user) {
      setFullName(user.user_metadata?.full_name || user.user_metadata?.name || '');
      setEmail(user.email || '');
    }
  }, [profile, user]);

  useEffect(() => {
    if (!user && !authLoading) {
      navigate('/auth');
      return;
    }
    
    if (user) {
      fetchMyPodcasts();
      fetchMyTours();
    }
  }, [user, authLoading, navigate]);

  useEffect(() => {
    fetchFavoritePodcasts();
  }, [favorites]);

  const handleSaveProfile = async () => {
    if (!user) return;
    
    setLoading(true);
    try {
      // Update profile
      const { error: profileError } = await supabase
        .from('profiles')
        .upsert({
          id: user.id,
          full_name: fullName,
          email: email,
        });

      if (profileError) throw profileError;

      // Update email if changed
      if (email !== user.email) {
        const { error: emailError } = await supabase.auth.updateUser({
          email: email
        });
        
        if (emailError) throw emailError;
        
        toast({
          title: 'Email updated',
          description: 'A verification email has been sent to your new address',
        });
      } else {
        toast({
          title: 'Profile updated',
          description: 'Your information has been saved',
        });
      }
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleLanguageChange = (newLanguage: string) => {
    const lang = newLanguage as 'FR' | 'EN';
    if (lang !== language) {
      toggleLanguage();
      setSelectedLanguage(lang);
      toast({
        title: 'Language updated',
        description: `Interface language changed to ${lang === 'EN' ? 'English' : 'French'}`,
      });
    }
  };

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map(word => word.charAt(0))
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const handlePodcastClick = (podcast: Podcast) => {
    if (podcast.Latitude && podcast.Longitude) {
      navigate('/map', { 
        state: { 
          focusLocation: {
            lat: podcast.Latitude,
            lng: podcast.Longitude,
            name: podcast.Name
          }
        }
      });
    }
  };

  const handleTourClick = (tour: CityTour) => {
    navigate('/tours', {
      state: {
        selectedTourId: tour.id
      }
    });
  };

  const handleBetaTesterClick = () => {
    setShowBetaDialog(true);
  };

  const handleBetaTesterToggle = async () => {
    if (!user) return;
    
    const newBetaTesterStatus = !betaTester;
    setLoading(true);
    
    try {
      const { error } = await supabase
        .from('profiles')
        .update({ beta_tester: newBetaTesterStatus })
        .eq('id', user.id);

      if (error) throw error;

      setBetaTester(newBetaTesterStatus);
      setShowBetaDialog(false);
      
      toast({
        title: newBetaTesterStatus ? 'Welcome to Beta Testing!' : 'Beta Testing Disabled',
        description: newBetaTesterStatus 
          ? 'Thank you for helping us improve the app!' 
          : 'You can rejoin anytime from your profile',
      });
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  if (!user) {
    return null;
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto px-4 py-8 max-w-6xl">
        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <Button 
            variant="ghost" 
            size="icon"
            onClick={() => navigate('/')}
            aria-label="Back to home"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <h1 className="text-3xl font-bold text-foreground">Profile</h1>
        </div>

        {/* Profile Header Card */}
        <Card className="mb-8 overflow-hidden">
          <CardContent className="p-8">
            <div className="flex flex-col items-center text-center space-y-4">
              <Avatar className="w-24 h-24">
                <AvatarImage src={profile?.avatar_url || undefined} />
                <AvatarFallback className="text-2xl font-bold bg-primary text-primary-foreground">
                  {getInitials(fullName || user?.email || 'U')}
                </AvatarFallback>
              </Avatar>
              <div>
                <h2 className="text-2xl font-bold">{fullName || 'Traveler'}</h2>
                <p className="text-muted-foreground">Explorer</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Stats Cards Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {/* Created Podcasts */}
          <Card className="hover:shadow-md transition-shadow cursor-pointer" onClick={() => {}}>
            <CardContent className="p-6">
              <div className="flex items-center space-x-4">
                <div className="p-3 bg-blue-100 dark:bg-blue-900/20 rounded-xl">
                  <Mic className="w-6 h-6 text-blue-600 dark:text-blue-400" />
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-sm text-muted-foreground">Podcasts Created</h3>
                  <p className="text-2xl font-bold">{myPodcasts.length}</p>
                </div>
                <ChevronRight className="w-4 h-4 text-muted-foreground" />
              </div>
            </CardContent>
          </Card>

          {/* Created Tours */}
          <Card className="hover:shadow-md transition-shadow cursor-pointer" onClick={() => {}}>
            <CardContent className="p-6">
              <div className="flex items-center space-x-4">
                <div className="p-3 bg-green-100 dark:bg-green-900/20 rounded-xl">
                  <Route className="w-6 h-6 text-green-600 dark:text-green-400" />
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-sm text-muted-foreground">Tours Created</h3>
                  <p className="text-2xl font-bold">{myTours.length}</p>
                </div>
                <ChevronRight className="w-4 h-4 text-muted-foreground" />
              </div>
            </CardContent>
          </Card>

          {/* Language Preferences */}
          <Card className="hover:shadow-md transition-shadow cursor-pointer">
            <CardContent className="p-6">
              <div className="flex items-center space-x-4">
                <div className="p-3 bg-purple-100 dark:bg-purple-900/20 rounded-xl">
                  <Globe className="w-6 h-6 text-purple-600 dark:text-purple-400" />
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-sm text-muted-foreground">Language</h3>
                  <Select value={selectedLanguage} onValueChange={handleLanguageChange}>
                    <SelectTrigger className="w-full mt-1 border-none p-0 h-auto focus:ring-0">
                      <SelectValue className="text-lg font-bold" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="EN">English</SelectItem>
                      <SelectItem value="FR">Français</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Beta Tester */}
          <Card 
            className={`hover:shadow-md transition-shadow cursor-pointer ${
              betaTester 
                ? 'border-orange-500 dark:border-orange-600 bg-orange-50/50 dark:bg-orange-900/10' 
                : 'border-orange-200 dark:border-orange-800'
            }`}
            onClick={handleBetaTesterClick}
          >
            <CardContent className="p-6">
              <div className="flex items-center space-x-4">
                <div className="p-3 bg-orange-100 dark:bg-orange-900/20 rounded-xl">
                  <Star className="w-6 h-6 text-orange-600 dark:text-orange-400" />
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-sm text-muted-foreground">Beta Tester</h3>
                  <p className="text-lg font-bold text-orange-600 dark:text-orange-400">
                    {betaTester ? 'Active' : 'Join Now'}
                  </p>
                </div>
                <ChevronRight className="w-4 h-4 text-muted-foreground" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Content Sections */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Created Podcasts Section */}
          <Card>
            <CardHeader className="pb-4">
              <CardTitle className="flex items-center gap-2">
                <Mic className="w-5 h-5 text-blue-600" />
                My Podcasts
                <Badge variant="secondary" className="ml-auto">{myPodcasts.length}</Badge>
              </CardTitle>
            </CardHeader>
            <CardContent>
              {myPodcasts.length === 0 ? (
                <div className="text-center py-12">
                  <Mic className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                  <p className="text-muted-foreground">No podcasts created yet</p>
                  <p className="text-sm text-muted-foreground mt-1">Start exploring places and create your first audio guide!</p>
                </div>
              ) : (
                <div className="space-y-3 max-h-96 overflow-y-auto">
                  {myPodcasts.map((podcast, index) => (
                    <div 
                      key={index} 
                      className="flex items-center space-x-4 p-4 rounded-lg hover:bg-accent/50 transition-colors cursor-pointer"
                      onClick={() => handlePodcastClick(podcast)}
                    >
                      <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-purple-600 rounded-lg flex items-center justify-center">
                        <Mic className="w-6 h-6 text-white" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="font-semibold truncate">{podcast.Name}</h3>
                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                          <MapPin className="w-3 h-3" />
                          <span className="truncate">{podcast.City || 'Unknown City'}</span>
                        </div>
                      </div>
                      <ChevronRight className="w-4 h-4 text-muted-foreground" />
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Created Tours Section */}
          <Card>
            <CardHeader className="pb-4">
              <CardTitle className="flex items-center gap-2">
                <Route className="w-5 h-5 text-green-600" />
                My Tours
                <Badge variant="secondary" className="ml-auto">{myTours.length}</Badge>
              </CardTitle>
            </CardHeader>
            <CardContent>
              {myTours.length === 0 ? (
                <div className="text-center py-12">
                  <Route className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                  <p className="text-muted-foreground">No tours created yet</p>
                  <p className="text-sm text-muted-foreground mt-1">Create your first custom city tour!</p>
                </div>
              ) : (
                <div className="space-y-3 max-h-96 overflow-y-auto">
                  {myTours.map((tour, index) => (
                    <div 
                      key={index} 
                      className="flex items-center space-x-4 p-4 rounded-lg hover:bg-accent/50 transition-colors cursor-pointer"
                      onClick={() => handleTourClick(tour)}
                    >
                      <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-blue-600 rounded-lg flex items-center justify-center">
                        <Route className="w-6 h-6 text-white" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="font-semibold truncate">{tour.title || tour.city}</h3>
                        <div className="flex items-center gap-4 text-sm text-muted-foreground">
                          <div className="flex items-center gap-1">
                            <MapPin className="w-3 h-3" />
                            <span className="truncate">{tour.country}</span>
                          </div>
                          <div className="flex items-center gap-1">
                            <Calendar className="w-3 h-3" />
                            <span>{formatDate(tour.created_at)}</span>
                          </div>
                        </div>
                      </div>
                      <ChevronRight className="w-4 h-4 text-muted-foreground" />
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Personal Information Section */}
        <Card className="mt-8">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Settings className="w-5 h-5" />
              Personal Information
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label htmlFor="fullName">Full Name</Label>
                <Input
                  id="fullName"
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  placeholder="Your full name"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="your@email.com"
                />
              </div>
            </div>
            <Button 
              onClick={handleSaveProfile} 
              disabled={loading}
              className="w-full md:w-auto"
            >
              <Save className="w-4 h-4 mr-2" />
              {loading ? 'Saving...' : 'Save Changes'}
            </Button>
          </CardContent>
        </Card>

        {/* Favorites Section */}
        {favoritePodcasts.length > 0 && (
          <Card className="mt-8">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Heart className="w-5 h-5 text-red-500" />
                Favorite Podcasts
                <Badge variant="secondary" className="ml-auto">{favoritePodcasts.length}</Badge>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {favoritePodcasts.map((podcast, index) => (
                  <div 
                    key={index} 
                    className="flex items-center space-x-3 p-4 rounded-lg border border-border hover:bg-accent/50 transition-colors cursor-pointer"
                    onClick={() => handlePodcastClick(podcast)}
                  >
                    <Heart className="w-4 h-4 text-red-500 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <h3 className="font-medium truncate">{podcast.Name}</h3>
                      <div className="flex items-center gap-1 text-sm text-muted-foreground">
                        <MapPin className="w-3 h-3" />
                        <span className="truncate">{podcast.City || 'Unknown City'}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Beta Tester Dialog */}
        <Dialog open={showBetaDialog} onOpenChange={setShowBetaDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Star className="w-5 h-5 text-orange-600" />
                Beta Tester Program
              </DialogTitle>
              <DialogDescription className="space-y-3 pt-4">
                <p>
                  As a beta tester, you'll get early access to new features and help shape the future of our app.
                </p>
                <p>
                  Your feedback is invaluable in making the experience better for everyone. You'll be able to test experimental features before they're released publicly.
                </p>
                <p>
                  Join our community of testers and be part of building something amazing!
                </p>
              </DialogDescription>
            </DialogHeader>
            <div className="flex gap-3 mt-4">
              <Button
                variant="outline"
                onClick={() => setShowBetaDialog(false)}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                onClick={handleBetaTesterToggle}
                disabled={loading}
                className="flex-1 bg-orange-600 hover:bg-orange-700"
              >
                {loading ? 'Updating...' : betaTester ? 'Leave Program' : 'Join Program'}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default Profile;